import { takeLatest, put, call, select, take } from 'redux-saga/effects';

import {
  getSurveysIfNeeded,
  getSurveyIfNeeded,
  getPaginatedSurveysIfNeeded,
  getMinimalSurveysIfNeeded,
  getSurveyResultsIfNeeded,
  createSurveyFromCurrent,
  updateSurveyFromCurrent,
  deleteSurveyByIdList,
  getSurveyLogoURLIfNeeded,
  saveSurveyLogoIfNeeded,
  getIndividualSurveyResultsIfNeeded,
  sendResponseForSurveyResult,
  getAssociatedCampaignsIfNeeded,
  getGuestDetailsIfNeeded,
  getSurveyResultCsvFile
} from './sagas';
import {
  GET_SURVEYS,
  GET_SURVEY,
  GET_PAGINATED_SURVEYS,
  GET_MINIMAL_SURVEYS,
  GET_SURVEY_RESULTS,
  CREATE_SURVEY,
  CREATING_SURVEY_SUCCEEDED,
  UPDATE_SURVEY,
  UPDATING_SURVEY_SUCCEEDED,
  DELETE_SURVEYS,
  getSurveys,
  getPaginatedSurveys,
  getIndividualSurveyResults,
  CREATING_SURVEY_FAILED,
  UPDATING_SURVEY_FAILED,
  GETTING_SURVEY_FAILED,
  GETTING_SURVEYS_FAILED,
  GETTING_SURVEY_RESULTS_FAILED,
  DELETING_SURVEYS_FAILED,
  SAVE_SURVEY_LOGO,
  GET_SURVEY_LOGO_URL,
  GET_INDIVIDUAL_SURVEY_RESULTS,
  GETTING_INDIVIDUAL_SURVEY_RESULTS_FAILED,
  SEND_SURVEY_RESULT_RESPONSE,
  SEND_SURVEY_RESULT_RESPONSE_SUCCEEDED,
  GET_ASSOCIATED_CAMPAIGNS,
  GET_GUEST_DETAILS,
  DELETING_SURVEYS_SUCCEEDED,
  GET_SURVEY_RESULT_CSV_FILE,
  UPDATE_EVENT_ID_FILTER
} from './actions';
import { actions as UserActions } from '../user';
import { actions as userMessaging } from '../userMessaging';

/* Helper Sagas */
function* forceGetAllSurveys() {
  yield put(getSurveys({ force: true }));
  yield put(getPaginatedSurveys({ force: true }));
}
function* waitForUserReady() {
  const userReady = yield select(store => store.user.ready);
  if (!userReady) {
    yield take(UserActions.LOGIN_READY);
  }
}

function* HandleFailuresSaga() {
  yield takeLatest(
    [
      CREATING_SURVEY_FAILED,
      UPDATING_SURVEY_FAILED,
      GETTING_SURVEY_FAILED,
      GETTING_SURVEYS_FAILED,
      GETTING_SURVEY_RESULTS_FAILED,
      GETTING_INDIVIDUAL_SURVEY_RESULTS_FAILED,
      DELETING_SURVEYS_FAILED
    ],
    function*(action) {
      yield put(
        userMessaging.setMessageForFailedAction({
          type: action.type,
          error: action.payload
        })
      );
    }
  );
}

/* APP FLOW - surveys */
function* surveyGetManySaga() {
  yield takeLatest(GET_SURVEYS, function*(action) {
    yield call(waitForUserReady);
    yield call(getSurveysIfNeeded, action);
  });
}
function* paginatedSurveyGetManySaga() {
  yield takeLatest(GET_PAGINATED_SURVEYS, function*(action) {
    yield call(waitForUserReady);
    yield call(getPaginatedSurveysIfNeeded, action);
  });
}
function* minimalSurveyGetManySaga() {
  yield takeLatest(GET_MINIMAL_SURVEYS, function*(action) {
    yield call(waitForUserReady);
    yield call(getMinimalSurveysIfNeeded, action);
  });
}
function* surveyGetSingleSaga() {
  yield takeLatest(GET_SURVEY, function*(action) {
    yield call(waitForUserReady);
    yield call(getSurveyIfNeeded, action);
  });
}
function* surveyGetSurveyResultsSaga() {
  yield takeLatest(GET_SURVEY_RESULTS, function*(action) {
    yield call(waitForUserReady);
    yield call(getSurveyResultsIfNeeded, action);
  });
}

function* surveyGetIndividualSurveyResultsSaga() {
  yield takeLatest(GET_INDIVIDUAL_SURVEY_RESULTS, function*(action) {
    yield call(waitForUserReady);
    yield call(getIndividualSurveyResultsIfNeeded, action);
  });
}

// brutally refresh to template list on any modification - this *could* be made to act just on the in-memory reducer instead.
function* surveyCreateSaga() {
  yield takeLatest(CREATE_SURVEY, createSurveyFromCurrent);
}
function* surveyUpdateSaga() {
  yield takeLatest(UPDATE_SURVEY, updateSurveyFromCurrent);
}

function* surveyCreateSuccessSaga() {
  yield takeLatest(CREATING_SURVEY_SUCCEEDED, forceGetAllSurveys);
}
function* surveyUpdateSuccessSaga() {
  yield takeLatest(UPDATING_SURVEY_SUCCEEDED, forceGetAllSurveys);
}
function* surveyDeleteSuccessSaga() {
  yield takeLatest(DELETING_SURVEYS_SUCCEEDED, forceGetAllSurveys);
}

function* surveyDeleteSaga() {
  yield takeLatest(DELETE_SURVEYS, function*(action) {
    yield call(deleteSurveyByIdList, action);
    // yield call(forceGetAllSurveys); // force refresh even if the delete is unsuccessful - since we don't know if we only partially failed.
  });
}

function* getSurveyLogoUrlSaga() {
  yield takeLatest(GET_SURVEY_LOGO_URL, function*(action) {
    yield call(getSurveyLogoURLIfNeeded, action);
  });
}

function* surveySaveLogoSaga() {
  yield takeLatest(SAVE_SURVEY_LOGO, function*(action) {
    yield call(saveSurveyLogoIfNeeded, action);
  });
}

function* sendSurveyResultResponseSaga() {
  yield takeLatest(SEND_SURVEY_RESULT_RESPONSE, function*(action) {
    yield call(sendResponseForSurveyResult, action);
  });
}

function* sendSurveyResultResponseSuccessSaga() {
  yield takeLatest(SEND_SURVEY_RESULT_RESPONSE_SUCCEEDED, function*() {
    const currentSurvey = store => (store.survey ? store.survey.current : {});
    let survey = yield select(currentSurvey);
    yield call(getIndividualSurveyResultsIfNeeded, { payload: survey });
  });
}

function* AssociatedCampaginsGetSaga() {
  yield takeLatest(GET_ASSOCIATED_CAMPAIGNS, function*(action) {
    yield call(getAssociatedCampaignsIfNeeded, action);
  });
}

function* GuestDetailsGetSaga() {
  yield takeLatest(GET_GUEST_DETAILS, function*(action) {
    yield call(getGuestDetailsIfNeeded, action);
  });
}

function* GetSurveyResultFileSaga() {
  yield takeLatest(GET_SURVEY_RESULT_CSV_FILE, getSurveyResultCsvFile);
}

function* GetSurveyResultForEventSaga() {
  yield takeLatest(UPDATE_EVENT_ID_FILTER, function*(action) {
    yield put(getIndividualSurveyResults({ force: true, forEvents: true }));
  });
}

export default [
  surveyGetManySaga,
  paginatedSurveyGetManySaga,
  minimalSurveyGetManySaga,
  surveyGetSingleSaga,
  surveyGetSurveyResultsSaga,
  surveyCreateSaga,
  surveyUpdateSaga,
  surveyDeleteSaga,
  surveySaveLogoSaga,
  getSurveyLogoUrlSaga,
  HandleFailuresSaga,
  surveyGetIndividualSurveyResultsSaga,
  sendSurveyResultResponseSaga,
  sendSurveyResultResponseSuccessSaga,
  surveyCreateSuccessSaga,
  surveyUpdateSuccessSaga,
  surveyDeleteSuccessSaga,
  AssociatedCampaginsGetSaga,
  GuestDetailsGetSaga,
  GetSurveyResultFileSaga,
  GetSurveyResultForEventSaga
];
